import type { ReactNode } from 'react'
import { clsx } from 'clsx'
import type { LucideIcon } from 'lucide-react'

interface PageHeaderProps {
  title: string
  subtitle?: string
  icon?: LucideIcon
  iconColor?: string
  coveragePct?: number
  loading?: boolean
  actions?: ReactNode
}

function CoverageBadge({ pct }: { pct: number }) {
  const cfg = pct >= 80
    ? { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400', ring: 'ring-emerald-500/20' }
    : pct >= 50
    ? { bg: 'bg-amber-500/10',   text: 'text-amber-400',   dot: 'bg-amber-400',   ring: 'ring-amber-500/20'   }
    : { bg: 'bg-red-500/10',     text: 'text-red-400',     dot: 'bg-red-400',     ring: 'ring-red-500/20'     }

  return (
    <span className={clsx(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1',
      cfg.bg, cfg.text, cfg.ring,
    )}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      Copertura dati: {pct.toFixed(0)}%
    </span>
  )
}

export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  iconColor = 'from-indigo-500 to-purple-500',
  coveragePct,
  loading = false,
  actions,
}: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
      <div className="flex items-center gap-4">
        {Icon && (
          <div className={clsx(
            'h-11 w-11 rounded-2xl flex items-center justify-center shrink-0',
            'bg-gradient-to-br shadow-lg',
            iconColor,
          )}
            style={{ boxShadow: '0 0 24px rgba(99,102,241,0.35), 0 4px 16px rgba(0,0,0,0.4)' }}
          >
            <Icon size={20} className="text-white" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-[--km-text-primary] tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-[--km-text-muted] mt-1">{subtitle}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {actions}
        {coveragePct !== undefined && !loading && (
          <CoverageBadge pct={coveragePct} />
        )}
      </div>
    </div>
  )
}
